import { useCallback, useEffect, useMemo } from "react";
import useFetchActionbarAuth from "src/hooks/useFetchActionbarAuth";
import { useToast } from "src/hooks/useToast";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { refetchUpdateList } from "src/utils/axios/reFetch";
import { downloadExcel, downloadSettingExcel } from "src/utils/blob/blobUtil";
import { formatDate, validateString } from "src/utils/string/stringUtil";
import { showSuccess, ShowSuccessFetch, showWarning } from "src/utils/toast/toastUtil";
import { createSYS4090_R01, deleteSYS4090_R01, downloadExcel_SYS4090_R01, getSYS4090_R01AllCount, getSYS4090_R01List, getSYS4090_R01One, patchSYS4090_R01 } from './Api_SYS4090_R01';
import { defaultSYS4090_R01Dto, SYS4090_R01Dto } from "./Dto_SYS4090_R01";
import { defaultSYS4090_R01Filter, SYS4090_R01Filter, useSYS4090_R01Store } from './Store_SYS4090_R01';
import { AgGridReact } from "ag-grid-react";
import { fetchDataSetHandler, fetchFilterConfig, gridRefSelectedRowHandler } from "src/utils/grid/GridHandlerContent";
import { useCommonCodeValidate } from 'src/utils/commonCode/CommonCodeValidateContext';
import {
	buildCommonCodeRequirements,
	SYS4090_R01_ROW_VALIDATE,
} from 'src/utils/commonCode/commonCodeRequirementConfig';


interface Props {
	gridRef: React.RefObject<AgGridReact<SYS4090_R01Dto> | null>;
    setMessage: (message: string) => void;
	
  }

const getParams = (filter: SYS4090_R01Filter) => {
	const params: any = {};
	if (filter.bankCd)     params.bankCd     = filter.bankCd;
	if (filter.acctType)    params.acctType   = filter.acctType;
	if (filter.includeDue)  params.includeDue = filter.includeDue;
	
            
	//기준일자는 만기포함여부가 아니오일때만 전송
	if (filter.baseDate && filter.includeDue === 'N') {
		params.baseDate = formatDate(filter.baseDate);
	}
	return params;
}
  
export const Content_SYS4090_R01 = (props: Props) => {
	const {
		gridRef,
		setMessage,
	} = props;

	const { fetchBankacctCdCode, compCdList, bankCdList, bankAcctTypeList, interesttypeList } = useCommonCodeStore();

    const { 
        SYS4090_R01List, setSYS4090_R01List, 
		SYS4090_R01PastFilter, setSYS4090_R01PastFilter,
		SYS4090_R01Filter, 
        selectedSYS4090_R01, setSelectedSYS4090_R01,
        editSYS4090_R01, setEditSYS4090_R01,
		allCount, setAllCount,
		initData,
		setScrollTop,
    } = useSYS4090_R01Store();
    const { setLoadingSpinner } = useLoadingSpinnerStore();
    const { success, error: showError } = useToast();
	const { validateCommonCodeLists } = useCommonCodeValidate();

    const handleExcelClick = useCallback(async () => {
        
		setLoadingSpinner(true);

		const response = await downloadExcel_SYS4090_R01(
			'',
			getParams(SYS4090_R01Filter),
			SYS4090_R01List.length ?? 0
		);
		downloadSettingExcel({ responseData: response.data.data, fileName: '부서 정보등록-excel.xlsx' });

		setLoadingSpinner(false);
    }, [SYS4090_R01Filter, setMessage, showError]);

	const renewalAllCount = useCallback(async () => {
		const allCountResponse = await getSYS4090_R01AllCount(getParams(SYS4090_R01Filter));
		setAllCount(allCountResponse.data.data);
		return allCountResponse.data.data;
	}, [SYS4090_R01Filter, setAllCount]);

    const fetchData = useCallback(async () => {
        setLoadingSpinner(true);
		setScrollTop(null);
        try {
			const responseData = await getSYS4090_R01List(
				'',
				getParams(SYS4090_R01Filter),
				SYS4090_R01List.length ?? 0
			);
			let newList = [...responseData.data.data ]
			setSYS4090_R01List(newList);
            
            // 백엔드 응답 구조에 맞게 데이터 추출
			const processAllCount = await renewalAllCount();
			if(newList.length > 0) {
				gridRefSelectedRowHandler(
					gridRef,
					setSelectedSYS4090_R01,
					() => true,// () => !accountData.find(value => value.bankAcctCd === selectedSYS4090_R01?.bankAcctCd)
				);
				setSYS4090_R01PastFilter(SYS4090_R01Filter);
				ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (총 ${newList.length}/${processAllCount}건)`);
				setTimeout(() => setMessage(""), 5000);
			} else {
				showWarning('조회된 데이터가 없습니다.', 3000);
				handleCreateClick();
			}
        } catch (error) {
            showError("조회 중 오류가 발생했습니다.");
        } finally {
            setLoadingSpinner(false);
        }
    }, [SYS4090_R01Filter, SYS4090_R01List, gridRef, editSYS4090_R01, setSYS4090_R01List, setSelectedSYS4090_R01, setSYS4090_R01PastFilter, showError, setLoadingSpinner, renewalAllCount, setScrollTop]);

	const defaultSYS4090_R01Row = useCallback(() => {
		
		const commonResult = validateCommonCodeLists(
			buildCommonCodeRequirements(SYS4090_R01_ROW_VALIDATE, {
				compCdList,
				bankCdList,
				bankAcctTypeList,
				interesttypeList,
			}),
		);
		if (!commonResult.valid) { return { ...defaultSYS4090_R01Dto }; }

		return {
			...defaultSYS4090_R01Dto,
			compCd: compCdList[0].compCd,
			bankCd: bankCdList[0].minorCd,
			acctType: bankAcctTypeList[0].minorCd,
			interesttype: interesttypeList[0].minorCd,
		};
	}, [defaultSYS4090_R01Dto, compCdList, bankCdList, bankAcctTypeList, interesttypeList, validateCommonCodeLists]);

	const fetchOne = useCallback(async () => {
		if (!selectedSYS4090_R01) return;
		setLoadingSpinner(true);

		try {
			const response = await getSYS4090_R01One(selectedSYS4090_R01.bankAcctCd);
			setEditSYS4090_R01({ ...response.data.data });
		} catch (error) {
			console.error("조회 중 오류 발생:", error);
			showError("조회 중 오류가 발생했습니다.");
		}

		setLoadingSpinner(false);
	}, [SYS4090_R01Filter, setEditSYS4090_R01, selectedSYS4090_R01]);

    const initContainerData = useCallback(async () => {
        // 전체 초기화: 검색 조건, 테이블, Info 패널 모두 초기화
        initData();
        setMessage("검색 조건 및 테이블이 모두 초기화되었습니다.");
    }, [initData, setMessage]);

    const handleCreateClick = useCallback(async () => {
		setSelectedSYS4090_R01(undefined);
        setEditSYS4090_R01({ ...defaultSYS4090_R01Row(), });
    }, [setEditSYS4090_R01, setSelectedSYS4090_R01]);

    const handleDeleteClick = useCallback(async () => {
            // 삭제 표시된 항목이 없으면 선택된 계좌를 삭제 목록에 추가
		if (!selectedSYS4090_R01) {
			showError("삭제할 계좌를 선택해주세요."); return;
		}

		if (window.confirm('정말로 삭제하시겠습니까?')) {
			await deleteSYS4090_R01(selectedSYS4090_R01.bankAcctCd);
			refetchUpdateList('delete', defaultSYS4090_R01Row(), selectedSYS4090_R01, 'bankAcctCd', SYS4090_R01List, setSYS4090_R01List);
			handleCreateClick();
			showSuccess(`${selectedSYS4090_R01.bankAcctCd} 계좌 삭제가 완료되었습니다.`)
		}
		
    }, [selectedSYS4090_R01, setSelectedSYS4090_R01, setEditSYS4090_R01, showError, setMessage]);

	const validateInsertData = (data: SYS4090_R01Dto) => {

		if (!validateString(data.bankAcctNm))	{ showWarning("계좌명을 입력해주세요.");			return false; }
		if (!validateString(data.bankAcctNo))	{ showWarning("실제 계좌번호를 입력해주세요.");		return false; }
		if (!validateString(data.bankCd))		{ showWarning("은행을 선택해주세요.");				return false; }
		if (!validateString(data.acctType))		{ showWarning("예적금구분을 선택해주세요.");		return false; }
		return true;
	}

    const handleSaveClick = useCallback(async () => {
        // 삭제할 항목도 없고 편집할 데이터도 없으면 에러
        if (!editSYS4090_R01) {
            showError("저장할 데이터가 없습니다.");
            return;
        }

        try {
            let operationMessage = "";
			let processName = '';
			let responseData: SYS4090_R01Dto = { ...defaultSYS4090_R01Row() };

            // 2. 등록처리
            if (editSYS4090_R01) {
                // 데이터 검증

				if(!validateInsertData(editSYS4090_R01)) return;

                if (selectedSYS4090_R01 && selectedSYS4090_R01.bankAcctCd) {
					processName = 'patch';
                    // 수정 모드 - U_01 API 사용
                    const response = await patchSYS4090_R01(editSYS4090_R01);
                    responseData = response.data.data[0];
                    operationMessage += "계좌 정보가 수정되었습니다.";
                } else {
                    // 등록 모드 - C_01 API 사용
					processName = 'post';
                    const response = await createSYS4090_R01(editSYS4090_R01);
                    responseData = response.data.data[0];
                    operationMessage += "새 계좌가 등록되었습니다.";
                }
            }

            
			showSuccess(operationMessage || "저장이 완료되었습니다.");
            setMessage(operationMessage || "저장이 완료되었습니다.");
			if(editSYS4090_R01) {

				refetchUpdateList(processName, responseData, editSYS4090_R01, 'bankAcctCd', SYS4090_R01List, setSYS4090_R01List);
			}
			// useCommonCodeStore의 은행계좌 리스트 업데이트
			await fetchBankacctCdCode();
            // await fetchData();
        } catch (error) {
            console.error("저장 중 오류 발생:", error);
            showError("저장 중 오류가 발생했습니다.");
        }
    }, [editSYS4090_R01, selectedSYS4090_R01, setLoadingSpinner, showError, setMessage, initData, fetchData, SYS4090_R01List, setSYS4090_R01List, fetchBankacctCdCode]);

    useFetchActionbarAuth("SYS4090_R01");


	useEffect(() => {
		if (selectedSYS4090_R01) {
			fetchOne();
		}else{
			setEditSYS4090_R01(defaultSYS4090_R01Row());
		}
	}, [selectedSYS4090_R01]);

    return {
        handleExcelClick,
        fetchData,
		defaultSYS4090_R01Row,
		fetchOne,
        initContainerData,
        handleCreateClick,
        handleDeleteClick,
        handleSaveClick,
    }
}